import { useNavigate } from "react-router-dom";
import { labelForStaffRole } from "../../constants/staffRoles";

// Helper to calculate age.
// A staff date of birth is OPTIONAL, and rows created while the field still
// carried `default=date.today` are stamped with the day they were made — those
// stamps were never rewritten, because a stamp cannot be told apart from a
// genuine entry. An age of 0 or less is therefore the signature of MISSING
// data, not of a person, and the banner shows nothing rather than "0 yrs".
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age > 0 ? age : null;
};

const StaffHeroBanner = ({
  staff,
  className = "",
  showAgent = true,
  isOnClickNavigate = false,
}) => {
  const navigate = useNavigate();

  // Calculate age
  const staffAge = calculateAge(staff?.date_of_birth);

  // Real club info — comes from the API as `club_details` (when staff.club is set)
  // or `current_organization` text field if user is not linked to a platform club.
  const getCurrentClubInfo = () => {
    if (staff?.club_details?.name) {
      return {
        name: staff.club_details.name,
        image: staff.club_details.logo || null,
        country: staff.club_details.country || null,
      };
    }
    if (staff?.current_organization) {
      return {
        name: staff.current_organization,
        image: null,
        country: null,
      };
    }
    if (staff?.works_for_club) {
      return { name: "Club Staff", image: null, country: null };
    }
    return { name: "Free Agent", image: null, country: null };
  };

  const currentClub = getCurrentClubInfo();
  // Display label for position — uses centralized constants/staffRoles.js so
  // a role_type like "president" renders as "President".
  const positionLabel =
    staff?.role_title_custom ||
    labelForStaffRole(staff?.role_type) ||
    labelForStaffRole(staff?.staff_type) ||
    "Staff";

  return (
    <div
      onClick={() => {
        if (isOnClickNavigate) {
          navigate(`/search/staff/${staff?.id}`);
        }
      }}
      className={`bg-gradient-to-r from-[#1A212F] to-[#374DF5] flex items-center rounded-xl text-white relative overflow-hidden h-auto min-h-[200px] md:h-[200px] cursor-pointer ${className}`}
    >
      {/* Main Content.
          Below md the row stacks and the detail tiles wrap, so nothing is
          pushed past the viewport — the same responsive treatment the
          structurally identical CoachHeroBanner already ships. */}
      <div className="w-full mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-8 flex items-center relative z-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center w-full">
          {/* Left Section - Image and Basic Info */}
          <div className="flex gap-4 md:gap-6 items-center">
            {/* Staff Image */}
            <div className="w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
              {staff?.document_url ? (
                <img
                  src={staff.document_url}
                  alt={staff.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                  <span className="text-gray-400 text-xl md:text-2xl font-bold">
                    {staff?.name?.charAt(0) || "S"}
                  </span>
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex flex-col justify-center min-w-0">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 text-white truncate">
                {staff?.name || "Staff Name"}
              </h1>
              <div className="space-y-1.5 md:space-y-2 text-sm md:text-base">
                <div className="flex gap-6 md:gap-10">
                  <span className="text-white/70 text-xs md:text-sm font-light">
                    Position:
                  </span>
                  <span className="text-xs md:text-sm font-light text-white/70">
                    {positionLabel}
                  </span>
                </div>
                {staffAge !== null && (
                  <div className="flex gap-6 md:gap-10">
                    <span className="text-white/70 text-xs md:text-sm font-light">
                      Profile:
                    </span>
                    <span className="text-xs md:text-sm font-light text-white/70">
                      {`${staffAge} yrs`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle Section - Key Details Cards */}
          <div className="flex flex-col gap-3 md:gap-4 ml-0 md:ml-auto w-full md:w-auto">
            {/* Club Card */}
            <div className="flex items-center gap-3 md:gap-4 flex-wrap">
              <div className="bg-white backdrop-blur-sm rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[140px] flex-1 md:flex-none">
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-[#1A212F]">Club:</span>
                  <div className="flex items-center gap-2 min-w-0">
                    {currentClub.image ? (
                      <img
                        src={currentClub.image}
                        alt=""
                        className="w-5 h-5 md:w-6 md:h-6 object-contain rounded-full flex-shrink-0"
                      />
                    ) : (
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">
                          {currentClub.name?.substring(0, 2).toUpperCase() ||
                            "CL"}
                        </span>
                      </div>
                    )}
                    <span className="text-xs md:text-sm font-bold text-[#1A212F] truncate">
                      {currentClub.name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Specialization Card */}
              <div className="bg-white backdrop-blur-sm rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[140px] md:min-w-[160px] flex-1 md:flex-none">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs md:text-sm text-[#1A212F] flex-shrink-0">
                    Specialization:
                  </span>
                  <span className="text-xs md:text-sm font-bold text-[#1A212F] truncate">
                    {staff?.additional_qualifications || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Country Flag - Bottom Right.
          Only render when the staff has a country set — no dummy gradient fallback. */}
      {staff?.country && (
        <div className="absolute right-4 md:right-6 bottom-4 md:bottom-6 flex items-center gap-2 md:gap-3">
          <div className="h-6 w-8 bg-gradient-to-r from-blue-500 via-white to-red-500 rounded-sm flex items-center justify-center">
            <span className="text-xs font-bold text-black">
              {staff.country.substring(0, 2).toUpperCase()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffHeroBanner;
